// Core imports
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Component imports
import { Row, Col, Spin, Button, Card, Tag, Typography, Progress, Descriptions, Space, Tooltip, Result } from 'antd';
import {
  ClockCircleOutlined, HeartOutlined, EyeOutlined, UserOutlined,
  LinkOutlined, BookOutlined,
  ExperimentOutlined, TeamOutlined, BulbOutlined, MedicineBoxOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import NdeTypeTag from '../components/Tags/NdeTypeTag';
import SEO from '../components/SEO/SEO';
import SupportPrompt from '../components/Misc/SupportPrompt';

// Misc imports
import { clearNdes, fetchNde } from '../store/ndes/ndesThunks';
import { useTranslation } from 'react-i18next';
import { NDE_TYPES, NAV_NDES, NAV_ROOT, SUPPORT_EMAIL } from '../constants';

// Asset imports
import NotFoundIcon from '../assets/images/not-found.png';
import PropTypes from 'prop-types';

const { Title, Text, Paragraph } = Typography;

// Consistent color palette (matches NdeTypeTag)
const TYPE_COLORS = {
  [NDE_TYPES.PEACEFUL]: '#52c41a',
  [NDE_TYPES.DISTRESSING]: '#ff4d4f',
  [NDE_TYPES.NEUTRAL]: '#90a4ae',
  [NDE_TYPES.MIXED]: '#fa8c16'
};

const sectionCardStyle = {
  borderRadius: 16,
  border: '1px solid #f0f0f0',
  boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
  height: '100%',
  transition: 'box-shadow 0.25s ease'
};

const sectionHeadStyle = {
  borderBottom: '1px solid #f0f0f0',
  fontSize: 15,
  fontWeight: 600,
  padding: '16px 24px'
};

const sectionBodyStyle = { padding: '18px 24px' };

const DetailItem = ({ label, value, extra }) => {
  if (value == null || value === '') {
    return null;
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Text>
      <div style={{ marginTop: 2 }}>{value}</div>
      {extra && <div style={{ marginTop: 4 }} className="text-capitalize-first">{extra}</div>}
    </div>
  );
};

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  extra: PropTypes.node
};

const YesNoTag = ({ value, t }) => {
  if (value === 'yes') {
    return <Tag color="green">{t('yes')}</Tag>;
  }
  if (value === 'no') {
    return <Tag color="red">{t('no')}</Tag>;
  }
  if (value) {
    return <Tag color="blue">{t(value)}</Tag>;
  }
  return <span>-</span>;
};

YesNoTag.propTypes = {
  value: PropTypes.string,
  t: PropTypes.func.isRequired
};

const BoolTag = ({ value }) => {
  if (value === true) {
    return <Tag color="green">Yes</Tag>;
  }
  if (value === false) {
    return <Tag color="red">No</Tag>;
  }
  return <span>-</span>;
};

BoolTag.propTypes = {
  value: PropTypes.bool
};

const NdeScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ndeId } = useParams();
  const { t } = useTranslation();
  const [showFullText, setShowFullText] = useState(false);

  const { nde, isFetchingNde, fetchNdeError } = useSelector((state) => state.ndes);

  useEffect(() => {
    if (!ndeId) {
      return navigate('/not-found', { replace: true });
    }

    dispatch(fetchNde(ndeId));

    // Cleanup
    return () => {
      dispatch(clearNdes());
    };
  }, [ndeId, dispatch, navigate]);

  const accentColor = nde ? (TYPE_COLORS[nde.type] || '#7c83fc') : '#7c83fc';

  // SEO metadata for individual NDE entry
  const ndeTitle = nde ? (nde.title || `${nde.type ? nde.type.charAt(0).toUpperCase() + nde.type.slice(1) : ''} Near-Death Experience #${ndeId}`) : '';
  const ndeDescPrefix = nde ? `${nde.type ? nde.type.charAt(0).toUpperCase() + nde.type.slice(1) + ' ' : ''}near-death experience${nde.deathCause ? ` caused by ${nde.deathCause}` : ''}${nde.graysonScale != null ? `, Greyson Scale: ${nde.graysonScale}/32` : ''}.` : '';
  const ndeDescText = nde?.text ? ' ' + nde.text.replace(/\s+/g, ' ').trim() : '';
  const ndeDescAvailable = Math.max(0, 155 - ndeDescPrefix.length);
  const ndeDescription = nde ? (ndeDescPrefix + (ndeDescText ? ndeDescText.substring(0, ndeDescAvailable).trimEnd() + (ndeDescText.length > ndeDescAvailable ? '…' : '') : '')).trim() : '';
  const ndeJsonLd = nde ? [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: ndeTitle,
      description: ndeDescription,
      url: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/ndes/${ndeId}`,
      publisher: {
        '@type': 'Organization',
        name: 'NDE Archive',
        url: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/ndes/${ndeId}`
      },
      ...(nde.type && { articleSection: nde.type }),
      ...(nde.createdAt && { datePublished: nde.createdAt }),
      ...(nde.updatedAt && { dateModified: nde.updatedAt })
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com' },
        { '@type': 'ListItem', position: 2, name: 'Near-Death Experiences', item: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/ndes` },
        { '@type': 'ListItem', position: 3, name: ndeTitle, item: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/ndes/${ndeId}` }
      ]
    }
  ] : null;

  const hasExperienceDetails = nde && (
    nde.environment?.length > 0 || nde.timePerception || nde.realityPerception
    || nde.hadObe || nde.hadLifeReview || nde.sawBrightLight != null
    || nde.feltSenseOfUnderstanding || nde.feltUnity
    || nde.sawHellishImagery != null || nde.hadSharedExperience != null
    || nde.sawTunnel != null || nde.heardMusic != null
  );

  const hasMedicalDetails = nde && (
    nde.deathCause || nde.deathDuration || nde.ndeDuration
    || nde.bodyFunctions?.length > 0 || nde.medication?.length > 0 || nde.dateOfNde
    || nde.experiencedClinicalDeath != null
    || nde.wasResuscitated != null || nde.hadAnesthesia != null || nde.hadPriorNde != null
  );

  const hasEntityDetails = nde && (
    nde.feltOtherEntities || nde.entityTypes?.length > 0
    || nde.hadVisionsOfFuture || nde.visionsCameTrue
    || nde.sawScenesFromElsewhere || nde.sawAnimals != null
  );

  const hasEmotionalDetails = nde && (
    nde.feltJudged || nde.hadChoiceToReturn
    || ((nde.type === NDE_TYPES.DISTRESSING || nde.type === NDE_TYPES.MIXED)
      && (nde.escapedNegativeNde || nde.feltDeservedNegativeNde))
    || nde.wasHealed || nde.emotions || nde.didNotWantToReturn != null
  );

  const hasPersonalInfo = nde && (
    nde.gender || nde.age || nde.sexuality
    || (nde.religionBefore && nde.religionNow)
    || nde.country || nde.ethnicity || nde.occupation
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px 48px' }}>
      <SEO
        title={ndeTitle || `Near-Death Experience #${ndeId}`}
        description={ndeDescription || `Read this verified near-death experience account on NDE Archive, the largest freely accessible database of NDEs.`}
        path={`/ndes/${ndeId}`}
        type="article"
        jsonLd={ndeJsonLd}
      />
      {isFetchingNde ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <Spin size="large" />
        </div>
      ) : fetchNdeError ? (
        <Result
          className="mb-64"
          icon={<img src={NotFoundIcon} alt="Not found" style={{ maxHeight: 400 }} />}
          title={t('NDE Not Found', { context: 'Title' })}
          subTitle={
            <>
              {t('Sorry, this NDE could not be found.', { context: 'Message' })}
              {SUPPORT_EMAIL && (
                <>
                  <br />
                  {' '}{t('If you need help, please contact us at', { context: 'Support prompt' })}{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                </>
              )}
            </>
          }
          extra={
            <Link to={NAV_ROOT}>
              <Button type="primary" className="bg-black-100 border-color-black-100 hover-bg-black-80 hover-border-color-black-80">
                {t('Return Home', { context: 'Call to action' })}
              </Button>
            </Link>
          }
        />
      ) : nde ? (
        <>
          {/* Back navigation */}
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(NAV_NDES)}
            style={{ marginBottom: 8, padding: '4px 0', color: '#7c83fc' }}
          >
            Back to NDEs
          </Button>

          {/* Hero Header */}
          <Card
            style={{
              borderRadius: 16,
              overflow: 'hidden',
              border: 'none',
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
              marginBottom: 24
            }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Accent bar */}
            <div style={{ height: 4, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}66)` }} />

            <div style={{ padding: '28px 32px 24px' }}>
              <Row gutter={[24, 16]} align="middle" justify="space-between">
                <Col flex="1">
                  <Space direction="vertical" size={8}>
                    <Space size={12} align="center" wrap>
                      <NdeTypeTag type={nde.type} style={{ fontSize: 13, padding: '2px 12px' }} />
                      {nde.isUnusual && (
                        <Tag color="purple" style={{ fontSize: 12 }}>Unusual NDE</Tag>
                      )}
                    </Space>
                    <Title level={2} style={{ margin: 0, fontWeight: 700, lineHeight: 1.2 }}>
                      {nde.title || 'Untitled Experience'}
                    </Title>
                    {nde.provider && (
                      <Space size={4}>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Source: {nde.provider}
                        </Text>
                        {nde.sourceLink && (
                          <a href={nde.sourceLink} target="_blank" rel="noopener noreferrer">
                            <LinkOutlined style={{ fontSize: 12, color: '#7c83fc' }} />
                          </a>
                        )}
                      </Space>
                    )}
                  </Space>
                </Col>

                {/* Greyson Scale Badge */}
                {nde.graysonScale != null && (
                  <Col>
                    <Tooltip title={nde.graysonScale >= 7 ? 'Score of 7+ indicates a genuine NDE on the Greyson Scale' : 'Greyson NDE Scale Score'}>
                      <div style={{ textAlign: 'center' }}>
                        <Progress
                          type="circle"
                          percent={Math.round((nde.graysonScale / 32) * 100)}
                          width={80}
                          strokeColor={nde.graysonScale >= 7 ? '#36d7b7' : '#ffb347'}
                          format={() => (
                            <div>
                              <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>{nde.graysonScale}</div>
                              <div style={{ fontSize: 10, color: '#999' }}>/32</div>
                            </div>
                          )}
                        />
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Greyson Scale</div>
                      </div>
                    </Tooltip>
                  </Col>
                )}
              </Row>

              {/* Quick stats row */}
              {(nde.deathCause || nde.ndeDuration || nde.deathDuration) && (
                <Row gutter={[16, 8]} style={{ marginTop: 20 }}>
                  {nde.deathCause && (
                    <Col>
                      <Tag icon={<MedicineBoxOutlined />} style={{ fontSize: 12, padding: '2px 10px' }}>
                        <span className="text-capitalize-first">{nde.deathCause}</span>
                      </Tag>
                    </Col>
                  )}
                  {nde.ndeDuration && (
                    <Col>
                      <Tag icon={<ClockCircleOutlined />} style={{ fontSize: 12, padding: '2px 10px' }}>
                        NDE: {nde.ndeDuration} min
                      </Tag>
                    </Col>
                  )}
                  {nde.deathDuration && (
                    <Col>
                      <Tag icon={<ClockCircleOutlined />} style={{ fontSize: 12, padding: '2px 10px' }}>
                        Unconscious: {nde.deathDuration} min
                      </Tag>
                    </Col>
                  )}
                </Row>
              )}
            </div>
          </Card>

          {/* NDE Description - Main Content */}
          <Card
            style={{ ...sectionCardStyle, marginBottom: 20, borderRadius: 16 }}
            headStyle={sectionHeadStyle}
            bodyStyle={{ ...sectionBodyStyle, padding: '20px 24px' }}
            title={
              <Space>
                <BookOutlined style={{ color: accentColor }} />
                <span>The Experience</span>
              </Space>
            }
          >
            <Paragraph
              className="white-space-pre-wrap text-capitalize-first"
              style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 8, color: '#333' }}
            >
              {showFullText ? nde.text : `${nde.text?.substring(0, 800)}... `}
            </Paragraph>
            {nde.provider !== 'NDERF' ? (
              <Button
                type="link"
                onClick={() => setShowFullText(!showFullText)}
                style={{ padding: 0, color: '#7c83fc', fontWeight: 500 }}
              >
                {showFullText ? 'Show less' : 'Show more'}
              </Button>
            ) : (
              <Space wrap>
                <a
                  href={nde.sourceLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#7c83fc', fontWeight: 500 }}
                >
                  Read more at the source <LinkOutlined />
                </a>
                {SUPPORT_EMAIL && (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=Broken NDE source link&body=NDE ID: ${nde.id}%0ASource link: ${encodeURIComponent(nde.sourceLink || '')}`}
                    style={{ fontSize: 12, color: '#aaa' }}
                  >
                    Broken source link? Contact us
                  </a>
                )}
              </Space>
            )}
          </Card>

          <Row gutter={[16, 16]} align="stretch">
            {/* Experience Details */}
            {hasExperienceDetails && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <EyeOutlined style={{ color: accentColor }} />
                      <span>Experience Details</span>
                    </Space>
                  }
                >
                  {nde.environment?.length > 0 && (
                    <DetailItem
                      label="Environment"
                      value={
                        <Space size={[4, 4]} wrap>
                          {(Array.isArray(nde.environment) ? nde.environment : nde.environment.split(',')).map((text, i) => (
                            <Tag key={i} color="blue" style={{ margin: 0 }}>{t(text.trim(), { context: 'environments' })}</Tag>
                          ))}
                        </Space>
                      }
                      extra={nde.environmentText}
                    />
                  )}
                  {nde.timePerception && (
                    <DetailItem
                      label="Time Perception"
                      value={<Tag>{t(nde.timePerception, { context: 'timePerceptions' })}</Tag>}
                      extra={nde.timePerceptionText}
                    />
                  )}
                  {nde.realityPerception && (
                    <DetailItem
                      label="Reality Perception"
                      value={<Tag>{t(nde.realityPerception, { context: 'realityPerceptions' })}</Tag>}
                      extra={nde.realityPercetionText}
                    />
                  )}
                  {nde.hadObe && (
                    <DetailItem
                      label="Out-of-Body Experience"
                      value={<YesNoTag value={nde.hadObe} t={t} />}
                      extra={nde.hadObeText}
                    />
                  )}
                  {nde.hadLifeReview && (
                    <DetailItem
                      label="Life Review"
                      value={<YesNoTag value={nde.hadLifeReview} t={t} />}
                      extra={nde.hadLifeReviewText}
                    />
                  )}
                  {nde.sawBrightLight != null && (
                    <DetailItem
                      label="Saw Bright Light"
                      value={<BoolTag value={nde.sawBrightLight} />}
                    />
                  )}
                  {nde.feltSenseOfUnderstanding && (
                    <DetailItem
                      label="Sense of Understanding"
                      value={<YesNoTag value={nde.feltSenseOfUnderstanding} t={t} />}
                      extra={nde.feltSenseOfUnderstandingText}
                    />
                  )}
                  {nde.feltUnity && (
                    <DetailItem
                      label="Felt Unity"
                      value={<YesNoTag value={nde.feltUnity} t={t} />}
                      extra={nde.feltUnityText}
                    />
                  )}
                  {nde.sawHellishImagery != null && (
                    <DetailItem
                      label="Hellish Imagery"
                      value={<BoolTag value={nde.sawHellishImagery} />}
                      extra={nde.sawHellishImageryText}
                    />
                  )}
                  {nde.hadSharedExperience != null && (
                    <DetailItem
                      label="Shared Experience"
                      value={<BoolTag value={nde.hadSharedExperience} />}
                      extra={nde.hadSharedExperienceText}
                    />
                  )}
                  {nde.sawTunnel != null && (
                    <DetailItem
                      label="Saw Tunnel"
                      value={<BoolTag value={nde.sawTunnel} />}
                      extra={nde.sawTunnelText}
                    />
                  )}
                  {nde.heardMusic != null && (
                    <DetailItem
                      label="Heard Music / Sounds"
                      value={<BoolTag value={nde.heardMusic} />}
                      extra={nde.heardMusicText}
                    />
                  )}
                </Card>
              </Col>
            )}

            {/* Medical & Physical Details */}
            {hasMedicalDetails && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <ExperimentOutlined style={{ color: accentColor }} />
                      <span>Medical & Physical</span>
                    </Space>
                  }
                >
                  {nde.dateOfNde && (
                    <DetailItem
                      label="Date of NDE"
                      value={new Date(nde.dateOfNde).toLocaleDateString()}
                    />
                  )}
                  {nde.deathCause && (
                    <DetailItem
                      label="Cause of Death"
                      value={<span className="text-capitalize-first">{nde.deathCause}</span>}
                    />
                  )}
                  {nde.deathDuration && (
                    <DetailItem
                      label="Duration of Physical Unconsciousness"
                      value={`${nde.deathDuration} minutes`}
                    />
                  )}
                  {nde.ndeDuration && (
                    <DetailItem
                      label="Subjective NDE Duration"
                      value={`${nde.ndeDuration} minutes`}
                    />
                  )}
                  {nde.bodyFunctions?.length > 0 && (
                    <DetailItem
                      label="Stopped Bodily Functions"
                      value={
                        <Space size={[4, 4]} wrap>
                          {(Array.isArray(nde.bodyFunctions) ? nde.bodyFunctions : nde.bodyFunctions.split(',')).map((text, i) => (
                            <Tag key={i} color="red" style={{ margin: 0 }}>{t(text.trim(), { context: 'bodyFunctions' })}</Tag>
                          ))}
                        </Space>
                      }
                    />
                  )}
                  {nde.medication?.length > 0 && (
                    <DetailItem
                      label="Medications / Drugs"
                      value={
                        <Space size={[4, 4]} wrap>
                          {nde.medication.map((med, i) => (
                            <Tag key={i} style={{ margin: 0 }}>{med}</Tag>
                          ))}
                        </Space>
                      }
                    />
                  )}
                  {nde.experiencedClinicalDeath != null && (
                    <DetailItem
                      label="Clinical Death"
                      value={<BoolTag value={nde.experiencedClinicalDeath} />}
                    />
                  )}
                  {nde.wasResuscitated != null && (
                    <DetailItem
                      label="Resuscitated"
                      value={<BoolTag value={nde.wasResuscitated} />}
                    />
                  )}
                  {nde.hadAnesthesia != null && (
                    <DetailItem
                      label="Under Anesthesia"
                      value={<BoolTag value={nde.hadAnesthesia} />}
                    />
                  )}
                  {nde.hadPriorNde != null && (
                    <DetailItem
                      label="Had Prior NDE"
                      value={<BoolTag value={nde.hadPriorNde} />}
                    />
                  )}
                </Card>
              </Col>
            )}

            {/* Entities & Visions */}
            {hasEntityDetails && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <TeamOutlined style={{ color: accentColor }} />
                      <span>Entities & Visions</span>
                    </Space>
                  }
                >
                  {nde.feltOtherEntities && (
                    <DetailItem
                      label="Felt Other Entities"
                      value={<YesNoTag value={nde.feltOtherEntities} t={t} />}
                      extra={nde.feltOtherEntitiesText}
                    />
                  )}
                  {nde.entityTypes?.length > 0 && (
                    <DetailItem
                      label="Entity Types"
                      value={
                        <Space size={[4, 4]} wrap>
                          {(Array.isArray(nde.entityTypes) ? nde.entityTypes : nde.entityTypes.split(',')).map((text, i) => (
                            <Tag key={i} color="purple" style={{ margin: 0 }}>{t(text.trim(), { context: 'entityTypes' })}</Tag>
                          ))}
                        </Space>
                      }
                    />
                  )}
                  {nde.hadVisionsOfFuture && (
                    <DetailItem
                      label="Visions of the Future"
                      value={<YesNoTag value={nde.hadVisionsOfFuture} t={t} />}
                      extra={nde.hadVisionsOfFutureText}
                    />
                  )}
                  {nde.visionsCameTrue && (
                    <DetailItem
                      label="Did Visions Come True"
                      value={<YesNoTag value={nde.visionsCameTrue} t={t} />}
                    />
                  )}
                  {nde.sawScenesFromElsewhere && (
                    <DetailItem
                      label="Saw Scenes from Elsewhere"
                      value={<YesNoTag value={nde.sawScenesFromElsewhere} t={t} />}
                      extra={nde.sawScenesFromElsewhereText}
                    />
                  )}
                  {nde.sawAnimals != null && (
                    <DetailItem
                      label="Saw Animals"
                      value={<BoolTag value={nde.sawAnimals} />}
                      extra={nde.sawAnimalsText}
                    />
                  )}
                </Card>
              </Col>
            )}

            {/* Emotional & Spiritual */}
            {hasEmotionalDetails && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <HeartOutlined style={{ color: accentColor }} />
                      <span>Emotional & Spiritual</span>
                    </Space>
                  }
                >
                  {nde.emotions && (
                    <DetailItem
                      label="Emotions"
                      value={
                        <Space size={[4, 4]} wrap>
                          {nde.emotions.split(',').map((e, i) => (
                            <Tag key={i} color="orange" style={{ margin: 0 }}>{e.trim()}</Tag>
                          ))}
                        </Space>
                      }
                    />
                  )}
                  {nde.feltJudged && (
                    <DetailItem
                      label="Felt Judged"
                      value={<Tag>{t(nde.feltJudged, { context: 'feltJudged' })}</Tag>}
                      extra={nde.feltJudgedText}
                    />
                  )}
                  {nde.hadChoiceToReturn && (
                    <DetailItem
                      label="Had Choice to Return"
                      value={<YesNoTag value={nde.hadChoiceToReturn} t={t} />}
                      extra={nde.hadChoiceToReturnText}
                    />
                  )}
                  {(nde.type === NDE_TYPES.DISTRESSING || nde.type === NDE_TYPES.MIXED) && nde.escapedNegativeNde && (
                    <DetailItem
                      label="Escaped Negative NDE"
                      value={<YesNoTag value={nde.escapedNegativeNde} t={t} />}
                      extra={nde.escapedNegativeNdeText}
                    />
                  )}
                  {(nde.type === NDE_TYPES.DISTRESSING || nde.type === NDE_TYPES.MIXED) && nde.feltDeservedNegativeNde && (
                    <DetailItem
                      label="Felt Deserved Negative NDE"
                      value={<YesNoTag value={nde.feltDeservedNegativeNde} t={t} />}
                      extra={nde.feltDeservedNegativeNdeText}
                    />
                  )}
                  {nde.wasHealed && (
                    <DetailItem
                      label="Was Healed"
                      value={<YesNoTag value={nde.wasHealed} t={t} />}
                      extra={nde.wasHealedText}
                    />
                  )}
                  {nde.didNotWantToReturn != null && (
                    <DetailItem
                      label="Did Not Want to Return"
                      value={<BoolTag value={nde.didNotWantToReturn} />}
                      extra={nde.didNotWantToReturnText}
                    />
                  )}
                </Card>
              </Col>
            )}

            {/* Personal Information */}
            {hasPersonalInfo && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <UserOutlined style={{ color: accentColor }} />
                      <span>Personal Information</span>
                    </Space>
                  }
                >
                  <Descriptions column={2} size="small" colon={false}>
                    {nde.gender && (
                      <Descriptions.Item label={<Text type="secondary">Gender</Text>}>
                        {t(nde.gender, { context: 'gender' })}
                      </Descriptions.Item>
                    )}
                    {nde.age && (
                      <Descriptions.Item label={<Text type="secondary">Age</Text>}>
                        {nde.age}
                      </Descriptions.Item>
                    )}
                    {nde.sexuality && (
                      <Descriptions.Item label={<Text type="secondary">Sexuality</Text>}>
                        {t(nde.sexuality)}
                      </Descriptions.Item>
                    )}
                    {nde.country && (
                      <Descriptions.Item label={<Text type="secondary">Country</Text>}>
                        {nde.country}
                      </Descriptions.Item>
                    )}
                    {nde.ethnicity && (
                      <Descriptions.Item label={<Text type="secondary">Ethnicity</Text>}>
                        {nde.ethnicity}
                      </Descriptions.Item>
                    )}
                    {nde.occupation && (
                      <Descriptions.Item label={<Text type="secondary">Occupation</Text>}>
                        {nde.occupation}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                  {nde.religionBefore && nde.religionNow && (
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Religion
                      </Text>
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag>{nde.religionBefore}</Tag>
                        <span style={{ color: '#bbb' }}>&rarr;</span>
                        <Tag color="blue">{nde.religionNow}</Tag>
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
            )}

            {/* Effects & Aftermath */}
            {(nde.ndeEffects || nde.developedPsychicAbilities != null || nde.lostFearOfDeath != null || nde.sharedExperiencePublicly != null) && (
              <Col xs={24} md={12}>
                <Card
                  style={sectionCardStyle}
                  headStyle={sectionHeadStyle}
                  bodyStyle={sectionBodyStyle}
                  title={
                    <Space>
                      <BulbOutlined style={{ color: accentColor }} />
                      <span>Effects & Aftermath</span>
                    </Space>
                  }
                >
                  {nde.developedPsychicAbilities != null && (
                    <DetailItem
                      label="Developed Psychic Abilities"
                      value={<BoolTag value={nde.developedPsychicAbilities} />}
                      extra={nde.developedPsychicAbilitiesText}
                    />
                  )}
                  {nde.lostFearOfDeath != null && (
                    <DetailItem
                      label="Lost Fear of Death"
                      value={<BoolTag value={nde.lostFearOfDeath} />}
                      extra={nde.lostFearOfDeathText}
                    />
                  )}
                  {nde.sharedExperiencePublicly != null && (
                    <DetailItem
                      label="Shared Experience Publicly"
                      value={<BoolTag value={nde.sharedExperiencePublicly} />}
                    />
                  )}
                  {nde.ndeEffects && (
                    <Paragraph className="text-capitalize-first" style={{ marginBottom: 0, lineHeight: 1.7 }}>
                      {nde.ndeEffects}
                    </Paragraph>
                  )}
                </Card>
              </Col>
            )}
          </Row>

          <SupportPrompt variant="nde" />
        </>
      ) : null}
    </div>
  );
};

export default NdeScreen;
